import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CreateCustomFieldDocument,
  type CreateCustomFieldInput,
  DeleteCustomFieldDocument,
  DuplicateCustomFieldDocument,
  GetIssueTypeDocument,
  UpdateCustomFieldDocument,
  type UpdateCustomFieldInput,
  UpdateIssueTypeDocument,
} from "#app/lib/data";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";
import { toastMutationError } from "#app/lib/utils/toast-errors";

export function useCustomFieldMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();
  const [createCustomFieldMutation] = useMutation(CreateCustomFieldDocument);
  const [updateCustomFieldMutation] = useMutation(UpdateCustomFieldDocument);
  const [deleteCustomFieldMutation] = useMutation(DeleteCustomFieldDocument);
  const [duplicateCustomFieldMutation] = useMutation(DuplicateCustomFieldDocument);

  const refetchList = useCallback(
    () => client.refetchQueries({ include: ["CustomFieldsListData"] }),
    [client],
  );

  const createCustomField = useCallback(
    async (input: CreateCustomFieldInput) => {
      try {
        const { data } = await createCustomFieldMutation({
          variables: { input },
          refetchQueries: ["CustomFieldsListData"],
        });
        const result = data?.createCustomField;
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        toastManager.add({ title: "Custom field created", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to create custom field");
        return null;
      }
    },
    [createCustomFieldMutation, track],
  );

  const updateCustomField = useCallback(
    async (id: string, input: UpdateCustomFieldInput) => {
      try {
        const { data } = await updateCustomFieldMutation({
          variables: { id, input },
        });
        const result = data?.updateCustomField;
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        toastManager.add({ title: "Custom field updated", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to update custom field");
        return null;
      }
    },
    [updateCustomFieldMutation, track],
  );

  const deleteCustomField = useCallback(
    async (id: string) => {
      try {
        const { data } = await deleteCustomFieldMutation({
          variables: { id },
        });
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "custom_field" });
        toastManager.add({ title: "Custom field deleted", type: "success" });
        return data?.deleteCustomField ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to delete custom field");
        return null;
      }
    },
    [deleteCustomFieldMutation, track],
  );

  const duplicateCustomField = useCallback(
    async (id: string) => {
      try {
        const { data } = await duplicateCustomFieldMutation({
          variables: { id },
          refetchQueries: ["CustomFieldsListData"],
        });
        const result = data?.duplicateCustomField;
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Duplicate, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        toastManager.add({ title: "Custom field duplicated", type: "success" });
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to duplicate custom field");
        return null;
      }
    },
    [duplicateCustomFieldMutation, track],
  );

  // Appends a field to each given project type, preserving that type's
  // existing fields (updateIssueType replaces the whole array). Best-effort
  // per type — a failure on one doesn't abort the rest; skips types that
  // already carry the field.
  const associateIssueTypes = useCallback(
    async (customFieldId: string, issueTypeIds: string[]) => {
      for (const issueTypeId of issueTypeIds) {
        try {
          const { data } = await client.query({
            query: GetIssueTypeDocument,
            variables: { id: issueTypeId },
            fetchPolicy: "cache-first",
          });
          const existing = (data?.issueType?.fields ?? []).map((f) => ({
            customFieldId: f.customFieldId,
            required: f.required,
          }));
          if (existing.some((f) => f.customFieldId === customFieldId)) continue;
          await client.mutate({
            mutation: UpdateIssueTypeDocument,
            variables: {
              id: issueTypeId,
              input: { fields: [...existing, { customFieldId, required: false }] },
            },
          });
        } catch (err) {
          toastMutationError(err, "Failed to add field to a project type");
        }
      }
    },
    [client],
  );

  return {
    createCustomField,
    updateCustomField,
    deleteCustomField,
    duplicateCustomField,
    associateIssueTypes,
    refetchList,
  };
}
